import { useCallback, useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

export const SIDEBAR_DEFAULT = 248;
export const SIDEBAR_MIN = 200;
export const SIDEBAR_MAX = 420;
const KEY = "admin-sidebar-width";
const KEY_STEP = 16;

const clamp = (value: number) => Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(value)));

function stored(): number {
  try {
    const value = Number(localStorage.getItem(KEY));
    return value ? clamp(value) : SIDEBAR_DEFAULT;
  } catch {
    return SIDEBAR_DEFAULT;
  }
}

// The width of the side menu, which the admin can drag wider or narrower by its
// right edge (or with the arrow keys once the edge is focused). It is
// remembered on this computer. A double-click puts it back to the default.
export function useResizableSidebar() {
  const [width, setWidth] = useState(stored);
  const dragging = useRef(false);
  const latest = useRef(width);

  const apply = useCallback((next: number, remember: boolean) => {
    const value = clamp(next);
    latest.current = value;
    setWidth(value);
    if (remember) {
      try {
        localStorage.setItem(KEY, String(value));
      } catch {
        // Storage blocked: the width still applies for this visit.
      }
    }
  }, []);

  const handleProps = {
    role: "separator" as const,
    "aria-orientation": "vertical" as const,
    "aria-label": "Resize the menu",
    "aria-valuemin": SIDEBAR_MIN,
    "aria-valuemax": SIDEBAR_MAX,
    "aria-valuenow": width,
    tabIndex: 0,
    title: "Drag to resize. Double-click to reset.",
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      dragging.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.userSelect = "none";
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      if (dragging.current) apply(event.clientX, false);
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      if (!dragging.current) return;
      dragging.current = false;
      event.currentTarget.releasePointerCapture(event.pointerId);
      document.body.style.userSelect = "";
      apply(latest.current, true);
    },
    onDoubleClick: () => apply(SIDEBAR_DEFAULT, true),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "ArrowLeft") apply(latest.current - KEY_STEP, true);
      else if (event.key === "ArrowRight") apply(latest.current + KEY_STEP, true);
      else if (event.key === "Home") apply(SIDEBAR_DEFAULT, true);
      else return;
      event.preventDefault();
    },
  };

  return { width, handleProps };
}
